import { MutableRefObject, useCallback, useMemo, useRef } from 'react';
import { Actions } from 'react-use/lib/useMap';
import memoize from 'lodash/memoize';
import pDebounce from 'p-debounce';

import useEvent from '@lyearn/core/hooks/useEvent';
import { UpdateQuestionEntity } from '@/types/schema';

type DirtyQuestionsRefType = Partial<Record<string, { fetching: boolean; dirty: boolean }>>;
type LocalUpdatedQuestionByIdState = Record<string, UpdateQuestionEntity>;

interface Params {
  // used to show that the pipeline validation is includes
  isPublishInProgressRef?: MutableRefObject<boolean>;
  // publish call to backend
  isPublishing: boolean;
  onUpdateQuestions: (questions: UpdateQuestionEntity[]) => Promise<any>;
  onPublish: () => Promise<void>;
  updatedQuestionById: LocalUpdatedQuestionByIdState;
  updateQuestionByIdActions: Actions<LocalUpdatedQuestionByIdState>;
}

// building these hooks generic
const useDebouncedQuestionsUpdates = (params: Params) => {
  const {
    isPublishInProgressRef,
    isPublishing,
    onUpdateQuestions,
    onPublish,
    updatedQuestionById,
    updateQuestionByIdActions,
  } = params;

  const dirtyQuestionsRef = useRef<DirtyQuestionsRefType>({});

  const setDirtyQuestionsRef = useCallback((updates: DirtyQuestionsRefType) => {
    const nextState = { ...dirtyQuestionsRef.current, ...updates };
    Object.keys(nextState).forEach((key) => {
      if (!updates[key]) delete nextState[key];
    });
    dirtyQuestionsRef.current = nextState;
  }, []);

  const getDebouncedUpdateQuestions = useMemo(() => {
    return () =>
      pDebounce(async (question: UpdateQuestionEntity) => {
        // incorrect logic creepying in, this publish logic should be handled outside
        if (!isPublishInProgressRef?.current) {
          await onUpdateQuestions([question]);
        }
        return question.id;
      }, 1500);
  }, [isPublishInProgressRef, onUpdateQuestions]);

  const debouncedUpdateQuestions = useMemo(
    () => memoize((questionId: string) => getDebouncedUpdateQuestions()),
    [getDebouncedUpdateQuestions],
  );

  // name it _onPublish or this shouldn't exist
  const onUpdateDirtyMutationsSuccess = useEvent(() => {
    if (!isPublishing || isPublishInProgressRef?.current) {
      return;
    }

    onPublish();
  });

  const updateQuestionIfDirty = useEvent((questionId: string) => {
    if (dirtyQuestionsRef.current[questionId]?.dirty && updatedQuestionById[questionId]) {
      setDirtyQuestionsRef({ [questionId]: { fetching: true, dirty: false } });
      debouncedUpdateQuestions(questionId)(updatedQuestionById[questionId]).then(
        updateQuestionIfDirty,
      );
    } else {
      // This marks that the question is saved and no longer dirty
      setDirtyQuestionsRef({ [questionId]: undefined });

      // Removing this line to retain the updated question in the map
      // updateQuestionByIdActions.remove(questionId);
    }

    // ??? auto publishing ?
    if (Object.keys(dirtyQuestionsRef.current).length === 0) {
      onUpdateDirtyMutationsSuccess();
    }
  });

  const setQuestion = useCallback(
    (question: UpdateQuestionEntity) => {
      updateQuestionByIdActions.set(question.id, question);

      if (dirtyQuestionsRef.current[question.id]?.fetching) {
        setDirtyQuestionsRef({ [question.id]: { fetching: true, dirty: true } });
      } else {
        setDirtyQuestionsRef({ [question.id]: { fetching: true, dirty: false } });
      }

      debouncedUpdateQuestions(question.id)(question).then(updateQuestionIfDirty);
    },
    [
      debouncedUpdateQuestions,
      dirtyQuestionsRef,
      setDirtyQuestionsRef,
      updateQuestionByIdActions,
      updateQuestionIfDirty,
    ],
  );

  return { onUpdateDirtyMutationsSuccess, setQuestion, dirtyQuestionsRef };
};

export default useDebouncedQuestionsUpdates;
